import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { updatePreferredLanguage } from "services/userService";
import { normalizeUserUiLanguage } from "constants/uiLocales";

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const getPreferredLanguage = () => {
    try {
      const token = localStorage.getItem("auth_token");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      // When logged in, prefer profile language over stale `selectedLanguage`
      if (token && user?.language) {
        return normalizeUserUiLanguage(user.language);
      }
    } catch (error) {
      // ignore
    }

    const savedLanguage = localStorage.getItem("selectedLanguage");
    if (savedLanguage) return savedLanguage;

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return normalizeUserUiLanguage(user?.language) || "en";
    } catch (error) {
      return "en";
    }
  };

  const [language, setLanguage] = useState(() => {
    return getPreferredLanguage();
  });
  const [translations, setTranslations] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Memoized function to load translations
  const loadTranslations = useCallback(async (lang) => {
    try {
      setIsLoading(true);
      const translationModule = await import(`../locales/${lang}/common.json`);
      setTranslations(translationModule.default || translationModule);
      localStorage.setItem("selectedLanguage", lang);
    } catch (error) {
      console.error(`Failed to load ${lang} translations:`, error);
      // Fallback to English
      try {
        const fallbackModule = await import("../locales/en/common.json");
        setTranslations(fallbackModule.default || fallbackModule);
      } catch (fallbackError) {
        console.error("Failed to load fallback translations:", fallbackError);
        setTranslations({});
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load translations when language changes
  useEffect(() => {
    if (language) {
      loadTranslations(language);
    }
  }, [language, loadTranslations]);

  // Stable setLanguage function
  const setLanguageStable = useCallback(
    async (lang, options = {}) => {
      const persist = options.persist !== false;
      if (lang !== language) {
        setLanguage(lang);
        if (persist) {
          const token = localStorage.getItem("auth_token");
          if (token) {
            try {
              await updatePreferredLanguage(lang);
              const user = JSON.parse(localStorage.getItem("user") || "{}");
              localStorage.setItem("user", JSON.stringify({ ...user, language: lang }));
            } catch (error) {
              console.error("Failed to persist language preference:", error);
            }
          }
        }
      }
    },
    [language]
  );

  const value = {
    language,
    setLanguage: setLanguageStable,
    translations,
    isLoading,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

LanguageProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};
